import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Loader2, Copy, CheckCircle2, Share2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { RecommendationCard } from "../components/RecommendationCard";
import { fetchPublicRecommendation, markShared } from "../lib/api";
import { SHARE } from "../constants/testIds";

export default function Share() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPublicRecommendation(id)
      .then((d) => setData(d))
      .catch((e) => setErr(e?.response?.status === 404 ? "Recommendation not found." : "Could not load this recommendation."));
  }, [id]);

  const copyLink = async () => {
    try {
      // Share the OG-enabled URL so socials show a rich preview.
      const url = `${window.location.origin}/api/share/${id}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      markShared(id).catch(() => {});
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // Clipboard unsupported — silently noop.
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid={SHARE.page}>
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 py-8">
        {err && (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-5">{err}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold"
            >
              Ask your own question
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {!err && !data && (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-20 justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
            Loading recommendation…
          </div>
        )}

        {data && (
          <>
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-widest text-emerald-500 font-semibold mb-2">
                Shared recommendation
              </div>
              <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-2">
                {data.query_summary || data.query}
              </h1>
              <p className="text-sm text-muted-foreground">
                A decision generated by ShouldIBuyIt.co.uk — an honest AI buying adviser for the UK.
              </p>
            </div>

            <RecommendationCard data={data.response} searchId={data.id} readOnly />

            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between bg-card border border-border rounded-2xl p-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                  Helpful so far
                </div>
                <div className="text-sm">
                  <span className="text-emerald-500 font-semibold">{data.helpful_votes}</span> helpful ·{" "}
                  <span className="text-red-400 font-semibold">{data.unhelpful_votes}</span> not ·{" "}
                  <span className="text-foreground font-semibold">{data.share_count}</span> shares
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={copyLink}
                  data-testid={SHARE.copyBtn}
                  className="flex items-center gap-1.5 text-xs px-3 py-2.5 rounded-lg border border-border hover:bg-secondary transition"
                >
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Link copied" : "Copy link"}
                </button>
                <button
                  onClick={() => navigate("/")}
                  data-testid={SHARE.ctaBtn}
                  className="flex items-center gap-1.5 text-xs px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition"
                >
                  Ask your own question
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        )}
      </main>
      <footer className="border-t border-border">
        <Footer />
      </footer>
    </div>
  );
}
